import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { Page, Text, View, Document, StyleSheet, Font, Image, pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import zioncom from 'images/system/zioncom.png'
import { App, Button, ButtonProps, Dropdown, Tooltip } from "antd";
import { ChangeEnum, OperateEnum, Order } from "@/types/inventory";
import { numFormmater } from "@/pages/Requirement/util";
import JsBarcode from "jsbarcode";
import convertTo2DArray from "@/utils/convert2double";
import { getFileUrl } from "@/apis/file";
import { DictContext } from "@/layout/context/DictContext";
import { OrderItemV2, OrderV2 } from "@/types/inventoryV2";
import { PurchaseOrder, PurchaseOrderItem } from "@/types/purchase";
import { changeTypes, zhVnChangeTypes } from "@/pages/Warehouse/utils";
import truncateString from "@/utils/stringOver";
import { getLocationIdx } from "@/utils/wLocation";
import { getRequirement } from "@/apis/requirement";
import { get } from "@/apis/order";
import { RequirementItem } from "@/types/requirement";
import { getSourceNumber } from "@/utils/getInventorySourceNo";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { dateFormat } from "@/utils/format";
import { upload } from "@/utils/file/upload";
import { docAdd } from "@/apis/doc";
import { DocTypeEnum } from "@/types/doc";
import { orderPrint, orderQcPrint } from "@/apis/inventoryv2";
import { computedPrint, initPrintSetting } from "@/layout/HomeLayout/utils";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

Font.registerHyphenationCallback((word: string) => ['', word, '']);

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black"
  },
  tableRow: {
    flexDirection: "row"
  },

  // 单元格基础样式
  tableCol: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 8,
    letterSpacing: -1,
    color: 'black'
  },

  // 表头单元格
  tableTopTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    letterSpacing: -1,
    color: 'black'
  },

  // 表头文本
  tableHeaderCell: {
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    fontSize: 8
  },

  // 合并单元格
  mergeCell: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: 0
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center'
  },

  // 页面标题
  pageTitle: {
    fontSize: 14,
    textAlign: 'center',
    letterSpacing: 0,
    paddingTop: 8,
    paddingBottom: 16,
  },
  // 页面标题
  subTitle: {
    fontSize: 8,
    textAlign: 'center',
    marginTop: -8,
    marginBottom: 2
  },

  // 标题区域容器
  titleSection: {
    position: 'relative' as any,
    marginBottom: 8,
  },

  // 右上角条码样式
  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: 0,
    flexDirection: 'column',
    alignItems: 'center'
  },

  // 条码文字
  barcodeText: {
    fontSize: 5,
    marginTop: -3
  },

  // 底部信息样式
  bottomInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    paddingHorizontal: 24
  },
  bottomItem: {
    flex: 1,
    textAlign: "left"
  },
  bottomText: {
    fontSize: 8
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 26,
    left: 0,
    right: 0,
    fontSize: 6,
  },
  printInfo: {
    position: "absolute",
    bottom: 26,
    right: 30,
    fontSize: 6,
    color: "#000",
    textAlign: "right",
  },

  footerItem: { width: '33.3%', fontSize: 6 },
  headerItem: { marginRight: 30, fontSize: 6, textAlign: 'left' },
  labelText: { fontSize: 10, textAlign: "center", marginTop: -14 },
  printCount: { fontSize: 8, position: "absolute", top: 12, right: 0, textAlign: "right" },
  printCountNormal: { fontSize: 8, position: "absolute", bottom: -2, right: 0, textAlign: "right" }
});

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

// 打印类型枚举
export enum PrintType {
  PURCHASE = "PURCHASE",
  NORMAL = "NORMAL",
  OUT = "OUT",
  SUP = "SUP"
}

interface PrintOrderListProps extends ButtonProps {
  selectedRows: Array<OrderItemV2>;
  disabled: boolean;
  onStart: () => void;
  onEnd: () => void;
  onPrinted?: (printCount: number) => void; // 打印完成回调
  dictAddress?: string;
  orderMark?: string;
  itemMark?: string;
  purOrder?: PurchaseOrder;
  showHeader?: boolean;
  printType: PrintType;
  purOrderNo?: string;
  showBottom?: boolean;
  btnText?: string;
  detailOrder?: OrderV2;
  clickValid?: () => boolean
}

type CodeUrlOrder = OrderItemV2 & {
  codeStr: string;
  codeUrl: string;
}

// 生成 PDF 下载链接
const PrintInventoryV2: FC<PrintOrderListProps> = ({
  selectedRows, disabled, onStart, onEnd, onPrinted, dictAddress,
  orderMark = '', itemMark = '', purOrder, showHeader = false, printType,
  purOrderNo, showBottom = false, btnText, detailOrder, clickValid = () => true,
  ...restProps
}) => {
  const { t } = useTranslation();
  const { printSetting } = useContext(DictContext);
  const curUser = useSelector((state: RootState) => state.userReducer.curUser);

  const { width: printWitdh, height: printHeight } = computedPrint();
  const { width: settingWitdh, height: settingHeight } = initPrintSetting();

  const { orderLabel, sourceReqId } = useMemo(() => {
    let order = selectedRows[0]?.order || detailOrder;
    let sourceReqId = (selectedRows[0]?.source as RequirementItem)?.orderId || '';
    let orderLabel = ''
    if (order) {
      let info = zhVnChangeTypes.find((item) => item.value === order.type);
      if (order.opType === OperateEnum.TEMPORARY) {
        orderLabel = [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(order.type)
          ? `${t('warehouse.tempIn', { lng: "zh" })}/${t('warehouse.tempIn', { lng: "vn" })}`
          : `${t('warehouse.otherOut', { lng: "zh" })}/${t('warehouse.otherOut', { lng: "vn" })}`
      } else orderLabel = info?.label ?? ''
    }
    return {
      sourceReqId,
      orderLabel
    }
  }, [selectedRows, t, changeTypes]);

  const printCountStr = useMemo(() => {
    const order = selectedRows[0]?.order ?? detailOrder;
    return (order?.printCount || 0) + 1 + '';
  }, [selectedRows, detailOrder])

  const printerStr = useMemo(() => {
    let time = dateFormat(dayjs(), true);
    if (!!curUser.name1 && !!curUser.name2) return `${curUser.name1}(${curUser.name2}) ${time}`
    else return `${curUser.name1 || curUser.name2 || curUser.mobile} ${time}`
  }, [curUser, selectedRows, detailOrder]);

  // useEffect(() => {
  //   if (!!sourceReqId && printType === PrintType.OUT) getInnerNo();
  // }, [sourceReqId, printType])


  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);
  const [innerNo, setInnerNo] = useState<string>('');

  const getInnerNo = async () => {
    setLoading(true);
    try {
      let requirement = await getRequirement(sourceReqId);
      let saleId = requirement.sourceId || '';
      let saleOrder = await get(saleId);
      setInnerNo(saleOrder.innerNumber || '');
    } catch (error) {
      console.log(">>>>>>没有销售单号");

    } finally {
      setLoading(false);
    }
  };

  const handleCode: (orderNo: string, lot: string) => Promise<{ codeUrl: string, codeStr: string }> = (orderNo: string, lot) => {
    return new Promise((resolve, reject) => {
      const canvasDom = document.createElement("canvas")
      JsBarcode(canvasDom, orderNo, {
        format: "CODE128",
        width: 10,
        height: 28,
        displayValue: false,
        margin: 0,
        marginBottom: 8
      })
      canvasDom.toBlob((blob) => {
        canvasDom.remove()
        if (blob) resolve({ codeUrl: URL.createObjectURL(blob), codeStr: lot })
        else reject("条码生成失败")
      }, 'image/png')

    })
  }

  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name };

  }, [printSetting])

  const getReceiverInfo = (order: PurchaseOrder | null) => {
    let supplierPrint = ""
    let mobilePrint = ""
    let recver = ""
    supplierPrint = order?.receiver || purOrder?.receiver || ''
    mobilePrint = order?.receiverMobile || purOrder?.receiverMobile || ''
    recver = order?.receiverContact || purOrder?.receiverContact || ''
    return {
      supplierPrint,
      mobilePrint,
      recver
    }
  }

  const MyNormalDocument = (selRows: CodeUrlOrder[], purOrderBarcode?: string) => {
    if (selRows && selRows.length > 0) {
      let seq: number = 0;
      let rows = selRows.sort((a, b) => {
        let vA = a?.product?.code || purOrder?.product?.code || '';
        let vB = b?.product?.code || purOrder?.product?.code || '';
        let numA = Number(vA) || 0;
        let numB = Number(vB) || 0;
        return numA - numB
      });

      return <Document>
        <Page size="A4" orientation="landscape" style={[styles.page, { padding: 28 }]} wrap>
          <View fixed>
            {/* 标题部分 */}
            {((printType === PrintType.PURCHASE && purOrderBarcode) || (printType === PrintType.OUT && showBottom && purOrderBarcode)) ? (
              <View style={styles.titleSection}>
                <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
                <Text style={styles.labelText}>{orderLabel}</Text>
                <View style={styles.titleBarcode}>
                  <Image src={purOrderBarcode} style={{ width: 100, height: 28 }} />
                  <Text style={styles.barcodeText}>{
                    printType === PrintType.PURCHASE ? purOrderNo :
                      printType === PrintType.OUT ? selectedRows[0]?.order?.number : ''
                  }</Text>
                </View>
                <Text style={styles.printCountNormal}>{`${t('order.printCount', { lng: "zh" })}(${t('order.printCount', { lng: "vn" })}): ${printCountStr}`}</Text>
              </View>
            ) : (
              <View style={styles.titleSection}>
                <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
                <Text style={styles.labelText}>{orderLabel}</Text>
                <View style={styles.titleBarcode}>
                  <Image src={purOrderBarcode} style={{ width: 100, height: 28 }} />
                  <Text style={styles.barcodeText}>{
                    printType === PrintType.PURCHASE ? purOrderNo :
                      printType === PrintType.OUT ? selectedRows[0]?.order?.number : (selRows?.[0]?.order?.number ?? '')
                  }</Text>
                </View>
                <Text style={styles.printCountNormal}>{`${t('order.printCount', { lng: "zh" })}(${t('order.printCount', { lng: "vn" })}): ${printCountStr}`}</Text>
              </View>
            )}
            {!!innerNo && <Text style={styles.subTitle}>{innerNo}</Text>}
          </View>
          <View style={{ ...styles.table, flexShrink: 0, borderLeftWidth: 0 }}>
            {/* 表头 */}
            {showHeader && <View style={{ flexDirection: 'row', borderLeftWidth: 1 }} >
              <View style={{ ...styles.tableTopTitle, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.sendTimePrint', { lng: "zh" })}(${t('purchase.sendTimePrint', { lng: "vn" })}):`, true)}{getBlankWord(rows[0]?.order.preTime ? dayjs(rows[0]?.order.preTime).format('YYYY-MM-DD') : '', true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.supplierPrint', { lng: "zh" })}(${t('purchase.supplierPrint', { lng: "vn" })}):` + getReceiverInfo((rows[0]?.source as PurchaseOrderItem)?.order || null).supplierPrint, true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, width: '29%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.mobilePrint', { lng: "zh" })}(${t('purchase.mobilePrint', { lng: "vn" })}):` + getReceiverInfo((rows[0]?.source as PurchaseOrderItem)?.order || null).mobilePrint, true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, width: '15%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.recver', { lng: "zh" })}(${t('purchase.recver', { lng: "vn" })}):` + getReceiverInfo((rows[0]?.source as PurchaseOrderItem)?.order || null).recver, true)}</Text></View>
            </View>}
            <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
              return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
            }}></View>
            <View style={{ flexDirection: 'row', borderLeftWidth: 1 }} fixed>
              <View style={{ ...styles.tableCol, width: '6%', borderTopWidth: 0 }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '22%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{
                  printType !== PrintType.OUT ? t('purchase.sendNum', { lng: "zh" })
                    : t('purchase.outLot', { lng: "zh" })
                }</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{
                  printType !== PrintType.OUT ? t('purchase.sendNum', { lng: "vn" })
                    : t('purchase.outLot', { lng: "vn" })
                }</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '15%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '24%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '8%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('warehouse.locationIndex', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('warehouse.locationIndex', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '6%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '9%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "vn" }))}</Text>
              </View>
            </View>
            {/* 表身 */}
            {rows.map((item, rowIndex) => (
              <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={rowIndex} wrap={false}>
                <View style={{ ...styles.tableCol, width: '6%' }}><Text style={styles.fontStyle}>{++seq}</Text></View>
                <View style={{ ...styles.tableCol, width: '22%', flexDirection: "column" }}>
                  {item.codeUrl && <Image src={item.codeUrl} style={{ width: "90%", height: 28 }} />}
                  <Text style={{ ...styles.fontStyle, letterSpacing: 0, fontSize: !!item.codeUrl ? 5 : 6, marginTop: !!item.codeUrl ? -5 : 0 }}>{getBlankWord(item.codeStr, true)}</Text>
                  <Text style={{ ...styles.fontStyle, letterSpacing: 0, fontSize: 5, marginTop: 2 }}>{getBlankWord(getSourceNumber(item), true)}</Text>
                </View>
                <View style={{ ...styles.tableCol, width: '15%' }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.code || purOrder?.product?.code || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '10%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', letterSpacing: -1 }}>{getBlankWord(item?.product?.name || purOrder?.product?.name || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '24%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(truncateString(item?.product?.model || purOrder?.product?.model || '', 38), true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '8%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(item?.locations?.map(loc => getLocationIdx(loc.location))?.join(',') || '', true)}</Text></View>

                <View style={{ ...styles.tableCol, ...styles.mergeCell, width: '15%', borderRightWidth: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'flex-start', width: '100%' }}>
                    <View style={{ ...styles.tableCol, width: '40.3%', borderTopWidth: 0, borderLeftWidth: 0, borderBottomWidth: 0 }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.uom || purOrder?.product?.uom || '')}</Text></View>
                    <View style={{ ...styles.tableCol, width: '59.7%', borderTopWidth: 0, borderRightWidth: 0, borderBottomWidth: 0 }}><Text style={{ ...styles.fontStyle, letterSpacing: 0 }}>{numFormmater(Number(item.count))}</Text></View>
                  </View>
                  <View style={{ borderStyle: "solid", borderTopWidth: 1, borderBottomWidth: 0, borderLeftWidth: 0, borderRightWidth: 0, borderColor: "black", width: "100%", color: "black", fontSize: 6, letterSpacing: -1, textAlign: 'left', padding: 2 }}>
                    {/* <Text>{getBlankWord(t('purchase.remark') ?? '', true) + "： "}{getBlankWord(orderMark && itemMark ? orderMark + '+' + itemMark : orderMark + itemMark, true)}</Text> */}
                    <Text>{getBlankWord(`${t('purchase.remark', { lng: "zh" })}(${t('purchase.remark', { lng: "vn" })})`, true) + "： "}{getBlankWord(item?.order?.desc ?? '', true)}</Text>
                  </View>
                </View>
              </View>
            ))}
            {showHeader && <View style={{ ...styles.tableRow }}>
              <View style={{ ...styles.tableCol, width: '100%', letterSpacing: -1, borderRightWidth: 1 }}>
                <Text style={{ textAlign: 'left', paddingBottom: 0, width: "100%", fontSize: 8 }}>{getBlankWord(`${t('purchase.sendAddress', { lng: "zh" })}(${t('purchase.sendAddress', { lng: "vn" })})：${dictAddress ?? purOrder?.receiverAddress ?? ''}`, true)}</Text>
              </View>
            </View>}
            {showHeader && <View style={{ ...styles.tableRow }}>
              <View style={{ ...styles.tableCol, width: '85%', letterSpacing: -1 }}>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, letterSpacing: 0 }}>送货要求：</Text></View>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, letterSpacing: 0 }}>1.按照规定送货日期送货。</Text></View>
              </View>
              <View style={{ ...styles.tableCol, width: '15%', borderRightWidth: 1 }}>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, textAlign: 'center', letterSpacing: 0 }}>收货人签字</Text></View>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, textAlign: 'center', letterSpacing: 0 }}>盖收货专用章</Text></View>
              </View>
            </View>}

          </View>

          {/* showBottom时显示底部信息 */}
          {showBottom && (
            <View style={styles.bottomInfo}>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>{
                  [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(rows[0].order.type) ? `${t('warehouse.inper', { lng: "zh" })}(${t('warehouse.inper', { lng: "vn" })}):` : `${t('warehouse.outper', { lng: "zh" })}(${t('warehouse.outper', { lng: "vn" })}):`
                }</Text>
              </View>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>{`${t('warehouse.wareper', { lng: "zh" })}(${t('warehouse.wareper', { lng: "vn" })}):`}</Text>
              </View>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>{`${t('warehouse.outCheckDate', { lng: "zh" })}(${t('warehouse.outCheckDate', { lng: "vn" })}):`}</Text>
              </View>
            </View>
          )}

          <Image fixed src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,  // 让图片填充剩余空间
            height: 30,
            objectFit: "scale-down" // 让图片保持比例填充
          }} />
          {/* 添加分页 */}
          <Text fixed style={{ ...styles.fontStyle, ...styles.footer, zIndex: 99 }} render={({ pageNumber, totalPages }) => {
            return `P ${pageNumber} / ${totalPages}`
          }}>
          </Text>
          <Text fixed style={styles.printInfo} >{printerStr}</Text>
        </Page>
      </Document>
    }
    else return <Document />
  };
  const MyVerticalDocument = (rows: CodeUrlOrder[]) => {
    let seq = 0;
    return (
      <Document>
        <Page size={{ width: printWitdh, height: printHeight }} orientation="portrait" style={[styles.page, { paddingHorizontal: 4 }]} wrap>
          <View fixed render={({ pageNumber }) => {
            return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000" }} />
          }}>
          </View>
          <View>
            {/* 标题部分 */}
            <View style={styles.titleSection}>
              <Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
              <Text style={styles.labelText}>
                {orderLabel}{'    '}
                {
                  printType === PrintType.PURCHASE ? purOrderNo : selectedRows[0]?.order?.number
                }
              </Text>
              <Text style={styles.printCount}>{`${t('order.printCount', { lng: "zh" })}(${t('order.printCount', { lng: "vn" })}): ${printCountStr}`}</Text>
            </View>
            {!!innerNo && <Text style={styles.subTitle}>{innerNo}</Text>}
          </View>
          <View style={{ ...styles.table, flexShrink: 0, borderLeftWidth: 0 }}>
            {/* 表头 */}
            {showHeader && <View style={{ flexDirection: 'row', borderLeftWidth: 1 }}>
              <View style={{ ...styles.tableTopTitle, padding: 0, flexDirection: "row", justifyContent: "flex-start", alignItems: "flex-start", fontSize: 8, width: '22%' }}>
                <View style={{ flexDirection: "column", paddingBottom: 4, paddingHorizontal: 2, borderRightWidth: 1 }}>
                  <Text>
                    {getBlankWord(`${t('purchase.sendTimePrint', { lng: "zh" })}:`, true)}
                  </Text>
                  <Text>
                    {getBlankWord(`${t('purchase.sendTimePrint', { lng: "vn" })}:`, true)}
                  </Text>
                </View>
                <View style={{ flex: 1, height: "100%", flexDirection: "column", justifyContent: "center" }}>
                  <Text style={{ width: "100%", textAlign: "center" }}>
                    {getBlankWord(rows[0]?.order.preTime ? dayjs(rows[0]?.order.preTime).format('YYYY-MM-DD') : '', true)}
                  </Text>
                </View>
              </View>
              <View style={{ ...styles.tableTopTitle, padding: 0, flexDirection: "row", justifyContent: "flex-start", alignItems: "flex-start", fontSize: 8, width: '24%' }}>
                <View style={{ flexDirection: "column", paddingBottom: 4, paddingHorizontal: 2, borderRightWidth: 1 }}>
                  <Text>
                    {getBlankWord(`${t('purchase.supplierPrint', { lng: "zh" })}:`, true)}
                  </Text>
                  <Text>
                    {getBlankWord(`${t('purchase.supplierPrint', { lng: "vn" })}:`, true)}
                  </Text>
                </View>
                <View style={{ flex: 1, height: "100%", flexDirection: "column", justifyContent: "center" }}>
                  <Text style={{ width: "100%", textAlign: "center" }}>
                    {getBlankWord(getReceiverInfo((rows[0]?.source as PurchaseOrderItem)?.order || null).supplierPrint, true)}
                  </Text>
                </View>
              </View>
              <View style={{ ...styles.tableTopTitle, padding: 0, flexDirection: "row", justifyContent: "flex-start", alignItems: "flex-start", fontSize: 8, width: '28%' }}>
                <View style={{ flexDirection: "column", paddingBottom: 4, paddingHorizontal: 2, borderRightWidth: 1 }}>
                  <Text>
                    {getBlankWord(`${t('purchase.mobilePrint', { lng: "zh" })}:`, true)}
                  </Text>
                  <Text>
                    {getBlankWord(`${t('purchase.mobilePrint', { lng: "vn" })}:`, true)}
                  </Text>
                </View>
                <View style={{ flex: 1, height: "100%", flexDirection: "column", justifyContent: "center" }}>
                  <Text style={{ width: "100%", textAlign: "center" }}>
                    {getBlankWord(getReceiverInfo((rows[0]?.source as PurchaseOrderItem)?.order || null).mobilePrint, true)}
                  </Text>
                </View>
              </View>
              <View style={{ ...styles.tableTopTitle, padding: 0, flexDirection: "row", justifyContent: "flex-start", alignItems: "flex-start", fontSize: 8, width: '26%' }}>
                <View style={{ flexDirection: "column", paddingBottom: 4, paddingHorizontal: 2, borderRightWidth: 1 }}>
                  <Text>
                    {getBlankWord(`${t('purchase.recver', { lng: "zh" })}:`, true)}
                  </Text>
                  <Text>
                    {getBlankWord(`${t('purchase.recver', { lng: "vn" })}:`, true)}
                  </Text>
                </View>
                <View style={{ flex: 1, height: "100%", flexDirection: "column", justifyContent: "center" }}>
                  <Text style={{ width: "100%", textAlign: "center" }}>
                    {getBlankWord(getReceiverInfo((rows[0]?.source as PurchaseOrderItem)?.order || null).recver, true)}
                  </Text>
                </View>
              </View>
            </View>}
            <View style={{ flexDirection: 'row', borderLeftWidth: 1 }}>
              <View style={{ ...styles.tableCol, width: '6%', borderTopWidth: 0 }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '15%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '10%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '34%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '14%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('warehouse.locationIndex', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('warehouse.locationIndex', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '8.45%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '12.55%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "vn" }))}</Text>
              </View>
            </View>
            {/* 表身 */}
            {rows.map((item) => (
              <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={seq} wrap={false}>
                <View style={{ ...styles.tableCol, width: '6%' }}><Text style={styles.fontStyle}>{getBlankWord((++seq) + '')}</Text></View>
                <View style={{ ...styles.tableCol, width: '15%' }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.code || purOrder?.product?.code || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '10%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', letterSpacing: -1 }}>{getBlankWord(item?.product?.name || purOrder?.product?.name || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '34%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(item?.product?.model || purOrder?.product?.model || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '14%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(item?.locations?.map(loc => getLocationIdx(loc.location))?.join(',') || '', true)}</Text></View>

                <View style={{ ...styles.tableCol, ...styles.mergeCell, width: '21%', borderRightWidth: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'flex-start', width: '100%' }}>
                    <View style={{ ...styles.tableCol, width: '40.3%', borderTopWidth: 0, borderLeftWidth: 0, borderBottomWidth: 0 }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.uom || purOrder?.product?.uom || '')}</Text></View>
                    <View style={{ ...styles.tableCol, width: '59.7%', borderTopWidth: 0, borderRightWidth: 0, borderBottomWidth: 0 }}><Text style={{ ...styles.fontStyle, letterSpacing: 0 }}>{numFormmater(Number(item.count))}</Text></View>
                  </View>
                  <View style={{ borderStyle: "solid", borderTopWidth: 1, borderBottomWidth: 0, borderLeftWidth: 0, borderRightWidth: 0, borderColor: "black", width: "100%", color: "black", fontSize: 8, letterSpacing: -1, textAlign: 'left', padding: 2 }}>
                    <Text>{getBlankWord(`${t('purchase.remark', { lng: "zh" })}(${t('purchase.remark', { lng: "vn" })})`, true) + "： " + getBlankWord(item?.order?.desc ?? '', true)}</Text>
                  </View>
                </View>
              </View>
            ))}
            {showHeader && <View style={{ ...styles.tableRow, borderLeftWidth: 1 }}>
              <View style={{ ...styles.tableCol, width: '100%', letterSpacing: -1, borderRightWidth: 1 }}>
                <Text style={{ textAlign: 'left', paddingBottom: 0, width: "100%", fontSize: 8 }}>{getBlankWord(`${t('purchase.sendAddress', { lng: "zh" })}(${t('purchase.sendAddress', { lng: "vn" })})：${dictAddress ?? purOrder?.receiverAddress ?? ''}`, true)}</Text>
              </View>
            </View>}
            {showHeader && <View style={{ ...styles.tableRow, borderLeftWidth: 1 }}>
              <View style={{ ...styles.tableCol, width: '85%', letterSpacing: -1 }}>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, letterSpacing: 0 }}>送货要求：</Text></View>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, letterSpacing: 0 }}>1.按照规定送货日期送货。</Text></View>
              </View>
              <View style={{ ...styles.tableCol, width: '15%', borderRightWidth: 1 }}>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, textAlign: 'center', letterSpacing: 0 }}>收货人签字</Text></View>
                <View style={{ width: "100%" }}><Text style={{ ...styles.tableHeaderCell, textAlign: 'center', letterSpacing: 0 }}>盖收货专用章</Text></View>
              </View>
            </View>}
          </View>

          {/* showBottom时显示底部信息 */}
          {showBottom && (
            <View style={styles.bottomInfo}>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>{
                  [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(rows[0].order.type) ? `${t('warehouse.inper', { lng: "zh" })}(${t('warehouse.inper', { lng: "vn" })}):` : `${t('warehouse.outper', { lng: "zh" })}(${t('warehouse.outper', { lng: "vn" })}):`
                }</Text>
              </View>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>{`${t('warehouse.wareper', { lng: "zh" })}(${t('warehouse.wareper', { lng: "vn" })}):`}</Text>
              </View>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>{`${t('warehouse.outCheckDate', { lng: "zh" })}(${t('warehouse.outCheckDate', { lng: "vn" })}):`}</Text>
              </View>
            </View>
          )}
          <View style={{ marginTop: 30, flexDirection: "row", justifyContent: "flex-end" }}>
            <Text style={{ bottom: 16, right: 4, fontSize: 8 }} >{printerStr}</Text>
          </View>
        </Page>
      </Document>
    )
  };



  const handlePDF = async (flag: boolean) => {
    if (selectedRows.length <= 0) {
      return notification.error({
        placement: 'top',
        message: t('common.addError'),
        description: t('purchase.printSelectTip'),
      });
    }
    let valid = clickValid();
    if (!valid) return

    if (!!sourceReqId && printType === PrintType.OUT) await getInnerNo();
    setLoading(true)
    onStart()

    try {
      // 生成采购单号或需求单号条码
      let purOrderBarcode: string | undefined;
      if (printType === PrintType.PURCHASE && purOrderNo) {
        try {
          const barcodeData = await handleCode(purOrderNo, purOrderNo);
          purOrderBarcode = barcodeData.codeUrl;
        } catch (error) {
          console.error('采购单号条码生成失败:', error);
        }
      } else if (printType === PrintType.OUT && showBottom && selectedRows[0]?.order?.number) {
        // 当是OUT类型且showBottom为true时，生成订单号条码
        try {
          const orderNumber = selectedRows[0].order.number;
          const barcodeData = await handleCode(orderNumber, orderNumber);
          purOrderBarcode = barcodeData.codeUrl;
        } catch (error) {
          console.error('订单号条码生成失败:', error);
        }
      }
      let codeRows: CodeUrlOrder[] = [];
      for (let i = 0; i < selectedRows.length; i++) {
        // 根据 printType 决定条码内容
        const barcodeContent = printType === PrintType.OUT ? selectedRows[i]?.lot ?? '' : selectedRows[i]?.order?.number;
        const displayText = printType === PrintType.OUT ? selectedRows[i]?.lot ?? '' : selectedRows[i]?.order?.number;

        let codeObj = await handleCode(barcodeContent || '-', displayText || '-');

        let codeRow: CodeUrlOrder = {
          ...selectedRows[i],
          codeStr: codeObj.codeStr,
          codeUrl: codeObj.codeUrl
        }
        codeRows.push({ ...codeRow })
      }
      if (!!!purOrderBarcode) {
        let number = selectedRows?.[0]?.order?.number ?? ''
        let inventory = await handleCode(number, number);
        purOrderBarcode = inventory.codeUrl
      }
      let pageEle = flag ? MyVerticalDocument(codeRows) : MyNormalDocument(codeRows, purOrderBarcode)
      if (!pageEle) {
        notification.error({ placement: 'top', message: t('purchase.printErr') });
        setLoading(false)
        return
      }
      // Generate PDF as Blob in the browser
      const blob = await pdf(pageEle).toBlob();

      let name = `${selectedRows[0].order.number}-${printerStr}(${flag ? t('warehouse.verticalPrint') : t('warehouse.normalPrint')}).pdf`;
      let docId = selectedRows[0].order.docId;
      if (docId) {
        let file = await upload({ name, size: blob.size, file: blob });
        if (file) {
          let doc = await docAdd({ pId: docId, name, suffix: file.suffix, nodeType: DocTypeEnum.LEAF, fileId: file.id });
          await orderPrint(selectedRows[0].orderId, doc.id);
          // 打印完成后，更新打印次数
          onPrinted?.((selectedRows[0].order.printCount ?? 0) + 1);
        }
      }
      const pdfUrl = URL.createObjectURL(blob);
      // 创建一个 iframe 并将 PDF URL 作为其源
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将 iframe 添加到 body 中
      document.body.appendChild(iframe);
      iframe.onload = () => {
        console.log('PDF 加载完成，等待额外时间');
        try {
          console.log('最终调用 print()');
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          // window.addEventListener("afterprint", handlePrintEnd);
        } catch (error) {
          console.error('打印失败:', error);
        } finally {
          setLoading(false)
          onEnd()
        }
      };

    } catch (error) {
      console.error('上传失败:', error);
      setLoading(false)
      onEnd()
    }
  };

  return (
    <Dropdown trigger={["click"]}
      menu={{
        items: [
          {
            key: 'NORM',
            label: t('warehouse.normalPrint'),
            onClick: () => handlePDF(false),

          },
          {
            key: 'WARE',
            label: <Tooltip title={t('layout.printTip', { width: settingWitdh, height: settingHeight })}>{t('warehouse.verticalPrint')}</Tooltip>,
            onClick: () => handlePDF(true),
          },
        ],
      }}
    >
      <Button disabled={disabled} type="primary" loading={loading} {...restProps}>{btnText ?? t('common.printInfo')}</Button>
    </Dropdown>
  );
};

export default PrintInventoryV2;